import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

import { api } from "../api/client";
import { Topic, useChanged } from "./changes";
import { queryClient } from "./queryClient";

// The largest page the API's paged lists give (`limit`, 1–100).
const PAGE = 100;

/**
 * Every item of a paged list (one whose GET takes `limit` and `offset`),
 * fetched a page at a time until a short page says there's no more: for a
 * view that counts all of them, where `usePagedApi` shows a page at a time.
 * Remembered as `useApi`'s answers are, so a page that comes back shows
 * them at once; `path` null fetches nothing.
 */
export function useWholeApi<T>(
  path: string | null,
  // What of yours it shows, so a change to it reloads it.
  about?: Topic[],
): {
  data: T[] | null;
  error: boolean;
  reload: () => void;
} {
  const query = useQuery<T[]>(
    {
      queryKey: ["whole", path],
      queryFn: async () => {
        const sep = (path as string).includes("?") ? "&" : "?";
        const all: T[] = [];
        for (;;) {
          const page = await api.get<T[]>(
            `${path}${sep}limit=${PAGE}&offset=${all.length}`,
          );
          all.push(...page);
          if (page.length < PAGE) return all;
        }
      },
      enabled: path !== null,
    },
    queryClient,
  );
  const reload = useCallback(() => {
    void queryClient.refetchQueries({ queryKey: ["whole", path], exact: true });
  }, [path]);
  useChanged(about, reload);

  return {
    data: path === null ? null : (query.data ?? null),
    error: query.isError && !query.isFetching,
    reload,
  };
}
